import React, { useState, useEffect, useCallback } from "react";
import {
  getData,
  getSeriesType,
  getconferenceEvent,
  getRevenueData,
  getRevenueByCountry
} from "../../../services/management/dashboard/mgmtDashboardServices";

import Sidebar from "../../../components/Layout/Sidebar";
import Header from "../../../components/Layout/Header";
import Footer from "../../../components/Layout/Footer";
import { Col, Container, Row } from "react-bootstrap";
import Card from "react-bootstrap/Card";
import TableSkeletonLoader from "../../../components/Loaders/TableSkeletonLoader";

// ── Currency config ───────────────────────────────────────────────────────────
const CURRENCIES = ["USD", "EUR", "INR", "GBP", "RUB", "AED", "THB"];

const CURRENCY_SYMBOL = {
  USD: "$", EUR: "€", INR: "₹", GBP: "£",
  RUB: "₽", AED: "د.إ", THB: "฿",
};

const CURRENCY_BG = {
   USD: "#1d4ed8", EUR: "#047857", INR: "#b45309",
  GBP: "#6d28d9", RUB: "#b91c1c", AED: "#0e7490", THB: "#be185d",
};

// Format with commas; "—" for zero/null
const fmt = (n) =>
  Number(n)
    ? Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })
    : "—";

// ─────────────────────────────────────────────────────────────────────────────
const MgmtDashboard = () => {

    const [seriesList, setSeriesList] = useState([]);
    const [dashboard, setDashboard] = useState([]);
    const [conferenceList, setConferenceList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [revenueData, setRevenueData] = useState([]);
    const [filters, setFilters] = useState({
      series: ""
    });
    const [revLoading,     setRevLoading]     = useState(false);
    const [countryData,    setCountryData]    = useState([]);
    const [countryLoading, setCountryLoading] = useState(false);


  // 🔽 Load Dashboard
  const loadDashboard = useCallback(async (seriesValue) => {
    try {
      setLoading(true);

      // await new Promise(resolve => setTimeout(resolve, 2000)); // ⏳ delay 2 sec

      const res = await getData({ series: seriesValue });

      const grouped = Object.values(
        (res || []).reduce((acc, row) => {
          const key = row.conf_shortname?.trim().toLowerCase();

          if (!acc[key]) {
           acc[key] = {
            conf_shortname: row.conf_shortname,
            single_nights: 0,
            double_nights: 0,
            triple_nights: 0,
           
            // ✅ ADD STATUS FIELDS
            waiting: 0,
            pending: 0,
            staging: 0,
            verified: 0,
            cancelled: 0,
             // waiting
            waiting_single: 0,
            waiting_double: 0,
            waiting_triple: 0,

            // pending
            pending_single: 0,
            pending_double: 0,
            pending_triple: 0,

            // staging
            staging_single: 0,
            staging_double: 0,
            staging_triple: 0,

            // verified
            verified_single: 0,
            verified_double: 0,
            verified_triple: 0,

            // cancelled
            cancelled_single: 0,
            cancelled_double: 0,
            cancelled_triple: 0,
          };
          }

          acc[key].single_nights += Number(row.single_nights) || 0;
          acc[key].double_nights += Number(row.double_nights) || 0;
          acc[key].triple_nights += Number(row.triple_nights) || 0;
        
          acc[key].waiting += Number(row.waiting) || 0;
          acc[key].pending += Number(row.pending) || 0;
          acc[key].staging += Number(row.staging) || 0;
          acc[key].verified += Number(row.verified) || 0;
          acc[key].cancelled += Number(row.cancelled) || 0;

          // waiting
          acc[key].waiting_single += Number(row.waiting_single) || 0;
          acc[key].waiting_double += Number(row.waiting_double) || 0;
          acc[key].waiting_triple += Number(row.waiting_triple) || 0;

          // pending
          acc[key].pending_single += Number(row.pending_single) || 0;
          acc[key].pending_double += Number(row.pending_double) || 0;
          acc[key].pending_triple += Number(row.pending_triple) || 0;

          // staging
          acc[key].staging_single += Number(row.staging_single) || 0;
          acc[key].staging_double += Number(row.staging_double) || 0;
          acc[key].staging_triple += Number(row.staging_triple) || 0;

          // verified
          acc[key].verified_single += Number(row.verified_single) || 0;
          acc[key].verified_double += Number(row.verified_double) || 0;
          acc[key].verified_triple += Number(row.verified_triple) || 0;

          // cancelled
          acc[key].cancelled_single += Number(row.cancelled_single) || 0;
          acc[key].cancelled_double += Number(row.cancelled_double) || 0;
          acc[key].cancelled_triple += Number(row.cancelled_triple) || 0;

          return acc;
        }, {})
      );

      setDashboard(grouped);
    } catch (err) {
      console.error("Dashboard error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  //load revenue
  const loadRevenue = useCallback(async (seriesValue) => {
    try {
      setRevLoading(true);
      const res = await getRevenueData({ series: seriesValue });
      setRevenueData(res || []);
    } catch (err) {
      console.error("Revenue error:", err);
    } finally {
      setRevLoading(false);
    }
  }, []);

  // 🏛️ Load Conferences
  const loadConferences = useCallback(async (seriesValue) => {
    try {
      const res = await getconferenceEvent();

      const filtered = res.filter(
        c => c.series?.trim().toLowerCase() === seriesValue?.trim().toLowerCase()
      );

      const unique = [...new Map(filtered.map(c => [c.shortname, c])).values()];
      
      const sorted = unique.sort((a, b) =>
        a.shortname?.toLowerCase().localeCompare(b.shortname?.toLowerCase())
      );

      setConferenceList(sorted);

      await loadDashboard(seriesValue);
    } catch (err) {
      console.error("Conference load error:", err);
    }
  }, [loadDashboard]);

  // 🔽 Load Series
  const loadSeries = useCallback(async () => {
    try {
      const res = await getSeriesType();

      const active = res.filter(s => s.is_active === "active");

      const unique = [
        ...new Map(active.map(item => [item.series, item])).values()
      ];

      const sorted = unique.sort((a, b) => {
        const [yearA, monthA] = a.series.split('-').map(Number);
        const [yearB, monthB] = b.series.split('-').map(Number);
        return yearA - yearB || monthA - monthB;
      });

      setSeriesList(sorted);

      if (sorted.length > 0) {
        setFilters({ series: sorted[0].series });
      }

    } catch (err) {
      console.error("Series load error:", err);
    }
  }, []);
  
  //load country count
    const loadRevenueByCountry = useCallback(async (seriesValue) => {
    try {
      setCountryLoading(true);
      const res = await getRevenueByCountry({ series: seriesValue });
      setCountryData(res || []);
    } catch (err) {
      console.error("Country revenue error:", err);
    } finally {
      setCountryLoading(false);
    }
  }, []);
  // ✅ Load series on mount
  useEffect(() => {
    loadSeries();
  }, [loadSeries]);

  // ✅ Load conferences when series changes
  useEffect(() => {
  if (filters.series) {
    loadConferences(filters.series);
    loadRevenue(filters.series);
    loadRevenueByCountry(filters.series);
  }
}, [filters.series, loadConferences, loadRevenue, loadRevenueByCountry]);

  // ➕ Totals
  const totals = dashboard.reduce(
    (acc, row) => ({
      single_nights: acc.single_nights + (Number(row.single_nights) || 0),
      double_nights: acc.double_nights + (Number(row.double_nights) || 0),
      triple_nights: acc.triple_nights + (Number(row.triple_nights) || 0),
    }),
    {
      single_nights: 0,
      double_nights: 0,
      triple_nights: 0,
    }
  );

  // revenue
   const revenueMap = revenueData.reduce((acc, row) => {
    const key = row.conf_shortname?.trim().toLowerCase();  // from conference_event.shortname via JOIN
    const cur = row.currency_type?.trim().toUpperCase();

    if (!key || !cur) return acc;

    if (!acc[key]) acc[key] = {};
    // Use += in case multiple rows per conf+currency (shouldn't happen but safe)
    acc[key][cur] = (acc[key][cur] || 0) + Number(row.total_amount || 0);

    return acc;
  }, {});

  // Grand total per currency across ALL conferences (shown in header row)
  const revGrandTotals = CURRENCIES.reduce((acc, cur) => {
    acc[cur] = revenueData
      .filter((r) => r.currency_type?.trim().toUpperCase() === cur)
      .reduce((s, r) => s + Number(r.total_amount || 0), 0);
    return acc;
  }, {});

  return (
    <Container fluid className="p-0">
      <Row>
        <Col>
          <div className="d-flex">
            <Sidebar />

            <div className="main-content w-100">
              <Header />

              <div className="container-fluid p-3">
                {/* series */}
                <Card className="mb-3">
                  <Card.Body>
                    <Row>
                      <Col md={4}>
                        <label>Series</label>
                        <select
                          className="form-control"
                          value={filters.series}
                          onChange={(e) =>
                            setFilters({ series: e.target.value })
                          }
                        >
                          <option value="">Select Series</option>
                          {seriesList.map((s, i) => {
                            const [year, month] = s.series.split('-').map(Number);
                            const label = new Date(year, month - 1)
                              .toLocaleString('en-US', { month: 'long', year: 'numeric' });

                            return (
                              <option key={i} value={s.series}>
                                {label}
                              </option>
                            );
                          })}
                        </select>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
                
                {/* accomodation details */}
                <Card>
                  <Card.Header className="bg-secondary text-white">
                    Accommodation Details
                  </Card.Header>

                  <Card.Body>
                    {loading ? (
                     [...Array(6)].map((_, i) => (   // 👈 6 rows
                        <tr key={i}>
                          <td colSpan="9">
                            <TableSkeletonLoader />
                          </td>
                        </tr>
                      ))                                      
                    ) : (
                    <div  style={{
                      overflowX: "auto",
                      // width: "100%",
                      WebkitOverflowScrolling: "touch"
                    }}>
                    <style>
                    {`
                      table td {
                        min-width: 55px;
                      }
                    `}
                    </style>
                    <table className="table table-sm table-bordered table-hover">
                    <colgroup>
                      <col style={{ width: "220px" }} />
                      {[...Array(15)].map((_, i) => (
                        <col key={i} style={{ width: "55px" }} />
                      ))}
                    </colgroup>

                    <thead>
                      <tr>
                        <th rowSpan="3" className="bg-secondary text-white text-center align-middle">
                          Conference
                        </th>

                        

                        <th colSpan="18" className="bg-secondary text-white text-center">
                          📊 Status
                        </th>
                      </tr>

                      <tr>
                        <th colSpan="3" className="bg-primary text-white text-center">
                          🌙 Nights
                        </th>
                       

                        {/* Status Groups */}
                        <th colSpan="3" className="bg-danger text-white text-center">Waiting</th>
                        <th colSpan="3" className="bg-warning text-white text-center">Pending</th>
                        <th colSpan="3" className="bg-warning text-white text-center">Staging</th>
                        <th colSpan="3" className="bg-success text-white text-center">Verified</th>
                        <th colSpan="3" className="bg-danger text-white text-center">Cancelled</th>
                      </tr>


                      <tr className="text-center">
                         {/* Nights */}
                        <th colSpan="1" className="text-center table-primary">Single</th>
                        <th colSpan="1" className="text-center table-primary">Double</th>
                        <th colSpan="1" className="text-center table-primary">Triple</th>
                        {/* Waiting */}
                        <th className="table-danger">S</th><th className="table-danger">D</th><th className="table-danger">T</th>

                        {/* Pending */}
                        <th className="table-warning">S</th><th className="table-warning">D</th><th className="table-warning">T</th>

                        {/* Staging */}
                        <th className="table-warning">S</th><th className="table-warning">D</th><th className="table-warning">T</th>

                        {/* Verified */}
                        <th className="table-success">S</th><th className="table-success">D</th><th className="table-success">T</th>

                        {/* Cancelled */}
                        <th className="table-danger">S</th><th className="table-danger">D</th><th className="table-danger">T</th>
                      </tr>
                    </thead>

                    <tbody>
                      {/* TOTAL ROW */}
                      <tr className="table-secondary fw-bold text-center">
                        <td className="text-center">Total</td>

                        <td className="bg-success text-light">{totals.single_nights}</td>
                        <td className="bg-success text-light">{totals.double_nights}</td>
                        <td className="bg-success text-light">{totals.triple_nights}</td>

                        {/* Waiting — red if value > 0, green if 0 */}
                        {["waiting_single","waiting_double","waiting_triple"].map((k, i) => {
                          const val = dashboard.reduce((a, b) => a + (Number(b[k]) || 0), 0);
                          return <td key={i} className={val > 0 ? "bg-danger text-light" : "bg-success text-light"}>{val}</td>;
                        })}

                        {/* Pending — red if value > 0, green if 0 */}
                        {["pending_single","pending_double","pending_triple"].map((k, i) => {
                          const val = dashboard.reduce((a, b) => a + (Number(b[k]) || 0), 0);
                          return <td key={i} className={val > 0 ? "bg-warning text-light" : "bg-success text-light"}>{val}</td>;
                        })}

                        {/* Staging — red if value > 0, green if 0 */}
                        {["staging_single","staging_double","staging_triple"].map((k, i) => {
                          const val = dashboard.reduce((a, b) => a + (Number(b[k]) || 0), 0);
                          return <td key={i} className={val > 0 ? "bg-warning text-light" : "bg-success text-light"}>{val}</td>;
                        })}

                        {/* Verified */}
                        <td className="bg-success text-light">{dashboard.reduce((a,b)=>a+(Number(b.verified_single)||0),0)}</td>
                        <td className="bg-success text-light">{dashboard.reduce((a,b)=>a+(Number(b.verified_double)||0),0)}</td>
                        <td className="bg-success text-light">{dashboard.reduce((a,b)=>a+(Number(b.verified_triple)||0),0)}</td>

                        {/* Cancelled */}
                        <td className="bg-success text-light">{dashboard.reduce((a,b)=>a+(Number(b.cancelled_single)||0),0)}</td>
                        <td className="bg-success text-light">{dashboard.reduce((a,b)=>a+(Number(b.cancelled_double)||0),0)}</td>
                        <td className="bg-success text-light">{dashboard.reduce((a,b)=>a+(Number(b.cancelled_triple)||0),0)}</td>
                      </tr>

                      {/* DATA ROWS */}
                      {conferenceList.length > 0 ? (
                        conferenceList.map((conf, i) => {
                          const row = dashboard.find(
                            d =>
                              d.conf_shortname?.trim().toLowerCase() ===
                              conf.shortname?.trim().toLowerCase()
                          );

                          return (
                            <tr key={i} className="text-center">
                              <td className="text-start">{conf.shortname}</td>

                              <td>{row?.single_nights ?? 0}</td>
                              <td>{row?.double_nights ?? 0}</td>
                              <td>{row?.triple_nights ?? 0}</td>

                              {/* Waiting */}
                              <td>{row?.waiting_single ?? 0}</td>
                              <td>{row?.waiting_double ?? 0}</td>
                              <td>{row?.waiting_triple ?? 0}</td>

                              {/* Pending */}
                              <td>{row?.pending_single ?? 0}</td>
                              <td>{row?.pending_double ?? 0}</td>
                              <td>{row?.pending_triple ?? 0}</td>

                              {/* Staging */}
                              <td>{row?.staging_single ?? 0}</td>
                              <td>{row?.staging_double ?? 0}</td>
                              <td>{row?.staging_triple ?? 0}</td>

                              {/* Verified */}
                              <td>{row?.verified_single ?? 0}</td>
                              <td>{row?.verified_double ?? 0}</td>
                              <td>{row?.verified_triple ?? 0}</td>

                              {/* Cancelled */}
                              <td>{row?.cancelled_single ?? 0}</td>
                              <td>{row?.cancelled_double ?? 0}</td>
                              <td>{row?.cancelled_triple ?? 0}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="19" className="text-center">
                            No data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                    </div>
                    )}
                  </Card.Body>
                </Card>
                
                {/* revenue */}
                <Card className="mt-3 mb-3">
                  <Card.Header className="bg-dark text-white">
                    💰 Revenue Details — by Conference &amp; Currency
                  </Card.Header>

                  <Card.Body>
                    {revLoading ? (
                      <table className="table table-sm">
                        <tbody>
                          {[...Array(6)].map((_, i) => (
                            <tr key={i}><td colSpan={CURRENCIES.length + 1}><TableSkeletonLoader /></td></tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                        <table className="table table-sm table-bordered table-hover text-center mb-1">
                          <colgroup>
                            <col style={{ width: "200px" }} />
                            {CURRENCIES.map((c) => <col key={c} style={{ width: "105px" }} />)}
                          </colgroup>

                          <thead>
                            {/* Row 1 — Currency name headers */}
                            <tr>
                              <th
                                rowSpan="2"
                                className="align-middle bg-dark text-white"
                                style={{ minWidth: "160px" }}
                              >
                                Conference
                              </th>
                              {CURRENCIES.map((cur) => (
                                <th
                                  key={cur}
                                  style={{
                                    backgroundColor: CURRENCY_BG[cur],
                                    color: "#fff",
                                    fontWeight: 700,
                                    letterSpacing: "0.04em",
                                  }}
                                >
                                  {CURRENCY_SYMBOL[cur]}&nbsp;{cur}
                                </th>
                              ))}
                            </tr>
                          </thead>

                          <tbody>
                          {/* TOTAL ROW */}
                          <tr className="fw-bold text-center table-secondary">
                            <td className="text-center">Total</td>
                            {CURRENCIES.map((cur) => (
                              <td
                                key={cur}
                                style={{
                                  backgroundColor: revGrandTotals[cur] > 0 ? CURRENCY_BG[cur] + "28" : "transparent",
                                  color: revGrandTotals[cur] > 0 ? "#111" : "#aaa",
                                }}
                              >
                                {fmt(revGrandTotals[cur])}
                              </td>
                            ))}
                          </tr>

                          {/* DATA ROWS */}
                          {conferenceList.length > 0 ? (
                            conferenceList.map((conf, i) => {
                              const key  = conf.shortname?.trim().toLowerCase();
                              const data = revenueMap[key] || {};

                              return (
                                <tr key={i}>
                                  <td className="text-start">{conf.shortname}</td>
                                  {CURRENCIES.map((cur) => (
                                    <td
                                      key={cur}
                                      style={{
                                        backgroundColor: data[cur] ? CURRENCY_BG[cur] + "18" : "transparent",
                                        color: data[cur] ? "#111" : "#aaa",
                                      }}
                                    >
                                      {fmt(data[cur])}
                                    </td>
                                  ))}
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={CURRENCIES.length + 1} className="text-center text-muted">
                                No revenue data available
                              </td>
                            </tr>
                          )}
                        </tbody>
                        </table>
                      </div>
                    )}
                  </Card.Body>
                </Card>

                {/* country count */}
                <Card className="mt-3 mb-3 col-md-6">
                  <Card.Header className="bg-dark text-white">
                    🌍 Revenue Leads by Country
                  </Card.Header>
                  <Card.Body>
                    {countryLoading ? (
                      [...Array(5)].map((_, i) => <TableSkeletonLoader key={i} />)
                    ) : countryData.length > 0 ? (
                      <>
                        {(() => {
                          const max = Math.max(...countryData.map(r => Number(r.count)));
                          return countryData.map((row, i) => (
                            <div key={i} className="mb-2">
                              <div className="d-flex justify-content-between mb-1">
                                <span className="fw-semibold">
                                  <span className="text-muted me-2">#{i + 1}</span>
                                  {row.country?.trim() || "Unknown"}
                                </span>
                                <span className="badge bg-info rounded-pill">{row.count}</span>
                              </div>
                              <div className="progress" style={{ height: "8px" }}>
                                <div
                                  className="progress-bar bg-success"
                                  style={{ width: `${(Number(row.count) / max) * 100}%` }}
                                />
                              </div>
                            </div>
                          ));
                        })()}
                        <hr />
                        {/* <div className="d-flex justify-content-between fw-bold">
                          <span>Total</span>
                          <span className="badge bg-dark rounded-pill">
                            {countryData.reduce((s, r) => s + Number(r.count), 0)}
                          </span>
                        </div> */}
                      </>
                    ) : (
                      <p className="text-center text-muted">No data available</p>
                    )}
                  </Card.Body>
                </Card>
                <Footer />
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default MgmtDashboard;